import apiClient, { API_PREFIX } from './client';
import { apiLogger } from '../utils/logger';

export type StickerStatus = 'available' | 'used' | 'cancelled';

export interface Sticker {
  id: string;
  stickerCode: string;
  status: StickerStatus | 'already_assigned';
  orderId?: string | null;
  usedAt?: string | null;
  createdAt: string;
  barcodeUrl?: string | null;
  barcodeBase64?: string | null;
}

export interface StickersResponse {
  success: boolean;
  stickers: Sticker[];
  pagination: {
    limit: number;
    offset: number;
    count: number;
  };
}

interface FetchStickerOptions {
  limit?: number;
  offset?: number;
  status?: string;
}

export async function fetchStickers(options: FetchStickerOptions = {}): Promise<Sticker[]> {
  const { limit = 50, offset = 0, status } = options;
  try {
    const response = await apiClient.get<StickersResponse>(`${API_PREFIX}/ems-stickers`, {
      params: {
        limit,
        offset,
        ...(status ? { status } : {}),
      },
    });
    return response.data.stickers;
  } catch (error) {
    apiLogger.error('[EMS API] fetchStickers error', error);
    throw error;
  }
}

export interface ImportStickersPayload {
  stickerCodes: string[];
}

export interface StickerInventorySummary {
  total: number;
  available: number;
  used: number;
  cancelled: number;
  lowStockAlert: boolean;
  alertThreshold: number;
  nextAvailable: Array<{
    id: string;
    stickerCode: string;
    createdAt: string;
  }>;
}

export async function createStickerBatch(payload: ImportStickersPayload) {
  try {
    const response = await apiClient.post<{
      success: boolean;
      importedCount: number;
      duplicateCount: number;
      errorCount: number;
      imported: Sticker[];
      duplicates: any[];
      errors: any[];
    }>(
      `${API_PREFIX}/ems-stickers/import`,
      payload,
    );
    return response.data.imported;
  } catch (error) {
    apiLogger.error('[EMS API] importStickers error', error);
    throw error;
  }
}

export async function updateStickerStatus(stickerId: string, status: StickerStatus) {
  try {
    await apiClient.patch(`${API_PREFIX}/ems-stickers/${stickerId}/status`, { status });
  } catch (error) {
    apiLogger.error('[EMS API] updateStickerStatus error', error);
    throw error;
  }
}

export async function fetchStickerInventory(): Promise<StickerInventorySummary> {
  try {
    const response = await apiClient.get<StickerInventorySummary>(`${API_PREFIX}/admin/stickers/inventory`);
    return response.data;
  } catch (error) {
    apiLogger.error('[EMS API] fetchStickerInventory error', error);
    throw error;
  }
}

export interface DeleteStickersResponse {
  success: boolean;
  deletedCount: number;
  deleted: string[];
  errorCount: number;
  errors: Array<{ id?: string; code?: string; error: string }>;
}

export async function deleteStickers(stickerIds: string[]): Promise<DeleteStickersResponse> {
  try {
    const response = await apiClient.post<DeleteStickersResponse>(`${API_PREFIX}/admin/stickers/delete-batch`, {
      stickerIds: stickerIds,
    });
    return response.data;
  } catch (error) {
    apiLogger.error('[EMS API] deleteStickers error', error);
    throw error;
  }
}
